import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Search as SearchIcon,
  DescriptionOutlined as DescriptionIcon,
} from '@material-ui/icons';
import moment from 'moment';
import DateMomentUtils from '@date-io/moment';
import clsx from 'clsx';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import styles from './ResultFilter.styles';
import FormField from '../../../../components/formField';
import { FieldUpdateArgs } from '../../../../core/types/form';
import {
  ResultDispatchFunc,
  SearchFilter,
  SetSearchFilterArgs,
  RequestListResult,
} from '../types';

const useStyles = makeStyles(styles);

type Props = {
  currentFilter?: SearchFilter;
  setSearchFilter: ResultDispatchFunc<SetSearchFilterArgs>;
  requestLists?: RequestListResult;
};

const ResultFilter: React.FC<Props> = ({
  currentFilter,
  setSearchFilter,
  requestLists,
}: Props) => {
  const classes = useStyles();
  const [patientSearchInput, setPatientSearchInput] = useState('');

  return (
    <MuiPickersUtilsProvider utils={DateMomentUtils}>
      <Box className={classes.root}>
        <Paper classes={{ root: classes.details }} square>
          <Grid item container spacing={2}>
            <Grid item container xs={12} sm={6} md={5} alignItems="center">
              <Box className={classes.boxInGrid}>
                <Typography variant="h6" className={classes.flexOne}>Date</Typography>
                <ButtonGroup
                  classes={{ root: classes.dateGroup }}
                  color="primary"
                  variant="outlined"
                  fullWidth
                >
                  <Button
                    variant={currentFilter?.dateFilter === 'all'
                      ? 'contained'
                      : 'outlined'}
                    onClick={() => setSearchFilter({
                      filter: currentFilter!,
                      path: 'dateFilter',
                      value: 'all',
                      toRequest: true,
                    })}
                  >
                    All
                  </Button>
                  <Button
                    variant={currentFilter?.dateFilter === 'today'
                      ? 'contained'
                      : 'outlined'}
                    onClick={() => setSearchFilter({
                      filter: currentFilter!,
                      path: 'dateFilter',
                      value: 'today',
                      toRequest: true,
                    })}
                  >
                    Today
                  </Button>
                  <Button
                    variant={currentFilter?.dateFilter === 'select'
                      ? 'contained'
                      : 'outlined'}
                    onClick={() => setSearchFilter({
                      filter: currentFilter!,
                      path: 'dateFilter',
                      value: 'select',
                      toRequest: true,
                    })}
                  >
                    Select
                  </Button>
                </ButtonGroup>
              </Box>
            </Grid>
            <Grid item container xs={12} sm={6} md={4} alignItems="center">
              <FormField
                fieldType="datePicker"
                className={classes.datePicker}
                label="From date"
                layout={{ xs: 6 }}
                format="DD-MM-YYYY"
                value={currentFilter?.fromDate || moment(new Date())}
                updateAction={(args: FieldUpdateArgs) => setSearchFilter({
                  filter: currentFilter!,
                  path: 'fromDate',
                  value: args.value
                    ? moment(args.value as Date).format('YYYY-MM-DD')
                    : '',
                  toRequest: true,
                })}
                isDisabled={currentFilter?.dateFilter !== 'select'}
              />
              <FormField
                fieldType="datePicker"
                className={classes.datePicker}
                label="To date"
                layout={{ xs: 6 }}
                format="DD-MM-YYYY"
                value={currentFilter?.toDate || moment(new Date())}
                updateAction={(args: FieldUpdateArgs) => setSearchFilter({
                  filter: currentFilter!,
                  path: 'toDate',
                  value: args.value
                    ? moment(args.value as Date).format('YYYY-MM-DD')
                    : '',
                  toRequest: true,
                })}
                isDisabled={currentFilter?.dateFilter !== 'select'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Search patient"
                value={patientSearchInput}
                onChange={(event) => {
                  if (event.target.value !== '') {
                    setPatientSearchInput(event.target.value);
                  } else {
                    setPatientSearchInput('');
                    if (currentFilter?.searchValue !== undefined && currentFilter?.searchValue !== '') {
                      setSearchFilter({
                        filter: currentFilter,
                        path: 'searchValue',
                        value: '',
                        toRequest: true,
                      });
                    }
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.stopPropagation();
                    event.preventDefault();
                    setSearchFilter({
                      filter: currentFilter!,
                      path: 'searchValue',
                      value: patientSearchInput,
                      toRequest: true,
                    });
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchFilter({
                          filter: currentFilter!,
                          path: 'searchValue',
                          value: patientSearchInput,
                          toRequest: true,
                        })}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item container xs={12} sm={6} md={5} alignItems="center">
              <Box className={classes.boxInGrid}>
                <Typography variant="h6" className={classes.flexOne}>Status</Typography>
                <ButtonGroup
                  classes={{ root: classes.dateGroup }}
                  color="primary"
                  variant="outlined"
                  fullWidth
                >
                  <Button
                    variant={currentFilter?.status === 'all'
                      ? 'contained'
                      : 'outlined'}
                    onClick={() => setSearchFilter({
                      filter: currentFilter!,
                      path: 'status',
                      value: 'all',
                      toRequest: true,
                    })}
                  >
                    All
                  </Button>
                  <Button
                    classes={{
                      root: clsx({
                        [classes.statusProcessButton]: currentFilter?.status === 'process',
                      }),
                    }}
                    variant={currentFilter?.status === 'process'
                      ? 'contained'
                      : 'outlined'}
                    onClick={() => setSearchFilter({
                      filter: currentFilter!,
                      path: 'status',
                      value: 'process',
                      toRequest: true,
                    })}
                  >
                    Process
                  </Button>
                  <Button
                    variant={currentFilter?.status === 'complete'
                      ? 'contained'
                      : 'outlined'}
                    color={currentFilter?.status === 'complete'
                      ? 'secondary'
                      : 'primary'}
                    onClick={() => setSearchFilter({
                      filter: currentFilter!,
                      path: 'status',
                      value: 'complete',
                      toRequest: true,
                    })}
                  >
                    Complete
                  </Button>
                </ButtonGroup>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <Box className={classes.des}>
          <Box>
            <Typography variant="body1" color="error">
              {currentFilter?.searchValue !== undefined && currentFilter?.searchValue !== ''
                ? `filter : ${currentFilter?.searchValue}`
                : ''}
            </Typography>
          </Box>
          <Box style={{ display: 'flex' }}>
            <DescriptionIcon color="action" style={{ marginRight: '8px' }} />
            <Typography variant="body1">
              {` ${requestLists?.count || 0} Items`}
            </Typography>
          </Box>
        </Box>
      </Box>
    </MuiPickersUtilsProvider>
  );
};

export default ResultFilter;
